import { RoomTypes } from '@weblite/ts-action/room'
import {
  CallType,
  CancelCall,
  JoinCall,
  RejectCall,
  reqCancelCall,
  reqJoinCall,
  reqRejectCall,
  reqStartCall,
  reqUpdateCall,
  StartCall,
  UpdateCall,
} from '@weblite/ts-action/media-proxy'
import cloneDeep from 'lodash.clonedeep'
import { Profiler } from '@weblite/profiler'
import { eventCallNameChanged } from '@weblite-wapps/protocol'
import { showErrorSnackbar } from '$comp/snackbars'
import { showCallFeedbackDialog } from '$comp/modals/call-feedback'
import { getHall, isPublicHall } from '$logic/hall'
import { getRoom, getRooms } from '$logic/room'
import { CallWappPanel } from '$logic/wapp-panels'
import { getClientUserId } from '$logic/client-user'
import { getHallPushData, getPvPushData } from '$logic/message'
import { closePvCallManager, openCall, openPvCallManager } from '$logic/router'
import { sendEventToWapp } from '$logic/wapp-communication/wapp-communication.publish'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { TWA_EVENTS, twaCommunication } from '$logic/twaCommunication'
import hostConf from '$config/frontend'
import { CallStorage, callStorage, CallStoragePvMeta } from './storage'
import {
  scheduleCall,
  unscheduleCall,
  updateScheduledCall,
} from '$logic/call/schedule.service'
import {
  getCallId,
  endOngoingCallWithConfirmation,
  isCallAtLeastOneMinuteLong,
  resetCallAndShowNPS,
} from './helpers'
import { request } from '$setup/ws'
import { ProfilerAction } from '$logic/profiler'
import { getClientUserIdentityToken } from '$logic/auth'
import { downloadFileFromUrl } from '$func/download'
import { t } from '$i18n'
import {
  CHECK_VPN_HOST,
  MEDIA_PROXY_API_URL,
  MEDIA_SERVER_API_URL,
} from '$config/env'
import { showCallRoomIsFullDialog } from '$comp/modals/call-room-is-full'

const CALL_TYPE_TO_START_EVENT_MAP: Record<CallType, string> = {
  call: 'CHAT_START_CALL',
  group_call: 'CHAT_START_MEETING',
  live: 'CHAT_START_LIVE',
  tribune: 'CHAT_START_TRIBUNE',
  classroom: 'CHAT_START_CLASSROOM',
}

const CALL_TYPE_TO_JOIN_EVENT_MAP: Record<CallType, string> = {
  call: 'CHAT_JOIN_CALL',
  group_call: 'CHAT_JOIN_MEETING',
  live: 'CHAT_JOIN_LIVE',
  tribune: 'CHAT_JOIN_TRIBUNE',
  classroom: 'CHAT_JOIN_CLASSROOM',
}

export const callServersConfig = {
  mediaProxyApiUrl: MEDIA_PROXY_API_URL,
  checkVPNHost: CHECK_VPN_HOST,
  recorderServerHost: 'http://localhost:3000',
  mediaServerApiUrl: MEDIA_SERVER_API_URL,
}

const joinCallProfiler = new Profiler(ProfilerAction.JOIN_CALL)

async function startCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
  name?: string,
) {
  const {
    roomId: ongoingCallRoomId,
    pvMeta: { roomId: pvMetaRoomId, state: pvCallState },
  } = callStorage.get()

  if (ongoingCallRoomId) await endOngoingCallWithConfirmation(roomId, callType)
  analite(CALL_TYPE_TO_START_EVENT_MAP[callType])

  if (callType === 'call') {
    // ClientUser wants to call a user which already is in call with
    if (ongoingCallRoomId === roomId) return openCall()

    // ClientUser wants to call a user which already is calling him
    if (pvMetaRoomId === roomId && pvCallState === 'outgoing')
      return openPvCallManager()

    callStorage.reset()
    callStorage.set({
      pvMeta: {
        roomId,
        state: 'outgoing',
        isCaller: true,
      },
    })

    openPvCallManager()
  }

  joinCallProfiler.start({ roomId, roomType, callType, isStartAction: true })
  const { error, payload } = await joinCallProfiler.runFunctionAsSubAction(
    async () =>
      request<StartCall>(
        reqStartCall({
          roomId,
          roomType,
          callType,
          push:
            roomType === 'PV' ? getPvPushData() : await getHallPushData(roomId),
          options: {
            name,
          },
        }),
      ),
    'get-token-request',
  )

  callStorage.set({ hasNextCall: false })

  if (error) {
    // TODO: Show error toast
    joinCallProfiler.reset()
  } else if (callType !== 'call') {
    const { error: resError, token, contentUri, serverAddress } = payload
    if (resError) {
      joinCallProfiler.reset()
      if (resError === 'USER_BANNED')
        showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

      return
    }

    const { name: hallName } = await getHall(roomId)
    const callName = name || hallName

    const sharedInput = {
      ...callServersConfig,
      title: callName,
      roomType,
      callType,
      mediaServerApiUrl: serverAddress || callServersConfig.mediaServerApiUrl,
    }

    if (token) {
      // NOTE: In classroom, wapp will stop the joinCallProfiler
      if (callType !== 'classroom')
        joinCallProfiler.stop({ callType, roomId, roomType })

      CallWappPanel.openCall(callType, token, {
        ...sharedInput,
        channelId: roomId,
        contentUri,
      })
    } else {
      joinCallProfiler.reset()
      return // TODO: Show Error snackbar
    }

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      joinedAt: new Date().getTime(),
      hallMeta: {
        name: callName,
        isOwner: true,
        isParticipating: true,
      },
    })
  } else {
    joinCallProfiler.stop()
    // handling Notification for call in PV
    twaCommunication.showCallNotification(
      roomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

async function getJoinRoomCredentials(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  const { error, payload } = await joinCallProfiler.runFunctionAsSubAction(
    () =>
      request<JoinCall>(
        reqJoinCall({
          roomId,
          roomType,
          callType,
        }),
      ),
    'get-token-request',
  )

  if (error) {
    joinCallProfiler.reset()
    showErrorSnackbar(t('snackbar.call.joinFailed')(t(`call.${callType}`)))
    return false
  }

  const { error: resError, token, contentUri, serverAddress } = payload
  if (resError) {
    joinCallProfiler.reset()
    if (resError === 'USER_BANNED')
      showErrorSnackbar(t('snackbar.call.userBanned')(t(`call.${callType}`)))

    if (resError === 'ROOM_FULL') {
      showCallRoomIsFullDialog(callType)
      return false
    }
    return false
  }

  return {
    token: token!,
    contentUri,
    serverAddress: serverAddress || callServersConfig.mediaServerApiUrl,
  }
}

async function joinCall(
  roomId: string,
  roomType: RoomTypes,
  callType: CallType,
) {
  if (callType === 'call') return
  const { roomId: ongoingCallRoomId } = callStorage.get()
  if (ongoingCallRoomId) await endOngoingCallWithConfirmation(roomId, callType)

  analite(CALL_TYPE_TO_JOIN_EVENT_MAP[callType])
  joinCallProfiler.start({ roomId, roomType, callType, isStartAction: false })

  const res = await getJoinRoomCredentials(roomId, roomType, callType)

  if (!res) return

  const { token, contentUri, serverAddress } = res

  const { name: hallName } = await getHall(roomId)
  const { call } = (await getRoom(roomId)) || {}
  const callName = call?.name || hallName

  if (token) {
    if (callType !== 'classroom') joinCallProfiler.stop()

    CallWappPanel.openCall(callType, token, {
      title: callName,
      startedAt: call?.startedAt,
      channelId: roomId,
      contentUri,
      roomType,
      callType,
      ...callServersConfig,
      mediaServerApiUrl: serverAddress,
    })
  } else {
    joinCallProfiler.reset()
    return // TODO: Show Error snackbar
  }

  callStorage.reset()
  callStorage.set({
    roomId,
    callType,
    startedAt: call?.startedAt,
    joinedAt: new Date().getTime(),
    hallMeta: { name: callName, isOwner: false, isParticipating: true },
  })

  twaCommunication.showCallNotification(
    roomId,
    TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    callType,
  )
}

export async function joinCallFromRouter(
  roomId: string,
  callType: CallType,
  title?: string,
  input?: Record<string, any>,
) {
  const { payload } = await request<JoinCall>(
    reqJoinCall({
      roomId,
      roomType: 'CHANNEL',
      callType,
    }),
  )
  const { token, contentUri, serverAddress } = payload || {}

  if (token) {
    CallWappPanel.openCall(callType, token, {
      title,
      contentUri,
      ...callServersConfig,
      ...input,
      channelId: roomId,
      mediaServerApiUrl: serverAddress || callServersConfig.mediaServerApiUrl,
      roomType: 'CHANNEL',
      callType,
    })

    callStorage.reset()
    callStorage.set({
      roomId,
      callType,
      startedAt: new Date().getTime(),
      joinedAt: new Date().getTime(),
      hallMeta: { name: callType, isOwner: false, isParticipating: true },
    })
  }
}

async function editCallName(name: string) {
  const { roomId, callType } = callStorage.get()
  if (!roomId || !callType) return

  const { type: roomType } = (await getRoom(roomId)) || {}
  if (!roomType) return

  const { error } = await request<UpdateCall>(
    reqUpdateCall({ roomId, roomType, callType, update: { name } }),
  )

  // TODO: handle error
  if (error) throw new Error()
  else callStorage.setHallMeta({ name })
}

async function sendNewNameToWapp(name: string) {
  if (!name) return

  callStorage.setHallMeta({ name })
  const wappMeta = CallWappPanel.getWappMeta()

  if (wappMeta.wappSessionId)
    sendEventToWapp(eventCallNameChanged({ name }, wappMeta))
}

async function cancelOutgoingCall() {
  const { roomId } = callStorage.getPvMeta()
  const { error } = await request<CancelCall>(
    reqCancelCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
  } else {
    callStorage.resetPvMeta()
    closePvCallManager()
  }
}

async function acceptIncomingCall() {
  const { roomId: ongoingCallRoomId } = callStorage.get()

  const pvMeta = callStorage.getPvMeta()
  const { roomId: incomingCallRoomId } = pvMeta

  if (ongoingCallRoomId)
    await endOngoingCallWithConfirmation(incomingCallRoomId, 'call')
  joinCallProfiler.start()
  callStorage.setPvMeta({ acceptingIncomingCall: true })

  const { error } = await request<JoinCall>(
    reqJoinCall({
      roomId: incomingCallRoomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
    joinCallProfiler.reset()
    callStorage.resetPvMeta()
    closePvCallManager()
  } else {
    callStorage.reset()
    callStorage.set({
      roomId: incomingCallRoomId,
      callType: 'call',
      pvMeta: {
        ...pvMeta,
        roomId: incomingCallRoomId,
      },
    })

    joinCallProfiler.stop({
      callType: 'call',
      roomId: incomingCallRoomId,
      roomType: 'PV',
    })
    twaCommunication.showCallNotification(
      incomingCallRoomId,
      TWA_EVENTS.SHOW_ONGOING_CALL_NOTIFICATION,
    )
  }
}

const rejectCallProfiler = new Profiler(ProfilerAction.REJECT_INCOMING_CALL)
async function rejectIncomingCall() {
  sendEventToTwa(TWA_EVENTS.REJECT_INCOMING_CALL)

  const { roomId } = callStorage.getPvMeta()

  rejectCallProfiler.start()
  const { error } = await request<RejectCall>(
    reqRejectCall({
      roomId,
      roomType: 'PV',
      callType: 'call',
    }),
  )

  if (error) {
    // TODO: handle error
    rejectCallProfiler.reset()
  } else {
    rejectCallProfiler.stop({
      roomId,
      roomType: 'PV',
      callType: 'call',
    })
    callStorage.resetPvMeta()
    closePvCallManager()
  }
}

const NPSShownCalls = new Set<string>()

async function getFeedbackWithScore(
  call: Pick<CallStorage, 'callType' | 'roomId'>,
  identifier?: string,
) {
  if (!call?.callType) return
  const feedback = await showCallFeedbackDialog(call.callType)

  analite('SUBMIT_CALL_SCORE', {
    section: call.callType,
    roomId: call.roomId,
    identifier,
    score: feedback.rating,
    feedback: feedback.problem,
    feedbackDetails: feedback.problemDescription,
  })
}

/**
 *
 * @param call should be a deepClone of CallStorage
 * @returns
 */
export async function showCallNPS(call: CallStorage) {
  if (!call.roomId || !call.callType) {
    return
  }

  const callId = getCallId(call)
  if (!callId || NPSShownCalls.has(callId) || !isCallAtLeastOneMinuteLong(call))
    return

  const isPublic = await isPublicHall(call.roomId)
  const identifier = isPublic ? call.roomId : undefined

  await getFeedbackWithScore(call, identifier)
  NPSShownCalls.add(callId)
}

export async function showCallNPSByWapp(call: {
  callType: CallType
  roomId: string
  startedAt: number
}) {
  const { roomId, callType } = call
  if (!roomId || !callType) return

  const callId = getCallId(call)
  if (
    !callId ||
    NPSShownCalls.has(callId) ||
    // startedAt in call wapp is the time that user joined that wapp.
    !isCallAtLeastOneMinuteLong({ joinedAt: call.startedAt })
  )
    return

  const isPublic = await isPublicHall(call.roomId)
  const identifier = isPublic ? call.roomId : undefined

  await getFeedbackWithScore(call, identifier)
  NPSShownCalls.add(callId)
}

async function endOngoingHallCall(callType: CallType) {
  sendEventToTwa(TWA_EVENTS.END_ONGOING_CALL)

  const call = callStorage.get()
  if (call.hasNextCall) return

  if (callType === 'group_call') await CallWappPanel.closeCall(false)

  if (callType !== 'classroom') {
    const clonedCall = cloneDeep(call)
    showCallNPS(clonedCall)
  }

  callStorage.reset()
}

async function endOngoingPvCall(
  duration?: number,
  status?: CallStoragePvMeta['status'],
) {
  sendEventToTwa(TWA_EVENTS.END_ONGOING_CALL)

  const call = callStorage.get()
  const clonedCall = cloneDeep(call)

  if (call.hasNextCall) return

  const { callType: ongoingCallType } = call
  if (ongoingCallType === 'call') {
    await CallWappPanel.closeCall(false)
    callStorage.reset()
  }

  const { roomId: pvMetaRoomId, isCaller: wasCaller } = callStorage.getPvMeta()

  if (
    status === 'canceled' ||
    (!wasCaller && (status === 'rejected' || status === 'timed-out'))
  ) {
    callStorage.resetPvMeta()
    closePvCallManager()
  } else {
    callStorage.setPvMeta({
      roomId: clonedCall.roomId || pvMetaRoomId,
      duration,
      status,
      state: 'ended',
    })

    openPvCallManager()
  }

  if (clonedCall.callType === 'call') showCallNPS(clonedCall)
}

async function checkForIncomingCalls() {
  const clientUserId = getClientUserId()
  const ringingPvs =
    getRooms().filter(
      ({ type, call }) =>
        type === 'PV' &&
        call?.type === 'call' &&
        clientUserId !== call.callerId &&
        !call.startedAt,
    ) || []

  const [ringingRoom] = ringingPvs
  if (ringingRoom) {
    callStorage.setPvMeta({
      roomId: ringingRoom.id,
      state: 'incoming',
      isCaller: false,
    })

    openPvCallManager()
  }
}

async function acceptIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.getPvMeta() || {}
  if (callRoomId && callRoomId === roomId) await acceptIncomingCall()
}

async function endOngoingCallByRoomId(roomId: string) {
  const { roomId: callRoomId, callType } = callStorage.get() || {}
  if (!callType || !callRoomId || callRoomId !== roomId) return

  if (callType === 'call') await endOngoingPvCall()
  else await endOngoingHallCall(callType)
}

async function rejectIncomingCallByRoomId(roomId: string) {
  const { roomId: callRoomId } = callStorage.getPvMeta() || {}
  if (callRoomId && callRoomId === roomId) resetCallAndShowNPS()
}

async function getCallAttendanceReport(uuid: string) {
  const authToken = await getClientUserIdentityToken()

  const url = `${hostConf.MEDIA_PROXY_SERVER}/report?uuid=${uuid}&token=${authToken}`
  downloadFileFromUrl(url, 'AttendanceList.xlsx')
}

export const callService = {
  startCall,
  joinCall,
  joinCallFromRouter,
  endOngoingHallCall,
  endOngoingPvCall,
  editCallName,
  sendNewNameToWapp,
  cancelOutgoingCall,
  acceptIncomingCall,
  rejectIncomingCall,
  checkForIncomingCalls,
  scheduleCall,
  unscheduleCall,
  updateScheduledCall,
  acceptIncomingCallByRoomId,
  endOngoingCallByRoomId,
  rejectIncomingCallByRoomId,
  getCallAttendanceReport,
  getJoinRoomCredentials,
}
